/**
 * MatchSharing Component
 *
 * Streamlined UI for sharing match links with collapsible instructions.
 */

import React, { useState } from 'react';
import styles from './MatchSharing.module.css';

export interface MatchSharingProps {
  matchId: string;
  currentPlayerId: 'player1' | 'player2';
}

export const MatchSharing: React.FC<MatchSharingProps> = ({ matchId, currentPlayerId }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // Generate match URLs
  const baseUrl = window.location.origin + window.location.pathname;
  const player1Url = `${baseUrl}?matchId=${matchId}&playerId=player1`;
  const player2Url = `${baseUrl}?matchId=${matchId}&playerId=player2`;

  const opponentPlayerId = currentPlayerId === 'player1' ? 'player2' : 'player1';
  const opponentUrl = currentPlayerId === 'player1' ? player2Url : player1Url;

  // Copy match link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(opponentUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Open opponent view in new window
  const handleOpenOpponent = () => {
    window.open(opponentUrl, '_blank', 'width=1400,height=900');
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainRow}>
        <div className={styles.linkSection}>
          <span className={styles.label}>Match Link:</span>
          <input
            type="text"
            value={opponentUrl}
            readOnly
            className={styles.linkInput}
            onClick={(e) => e.currentTarget.select()}
          />
        </div>

        <div className={styles.actions}>
          <button onClick={handleCopyLink} className={styles.copyButton}>
            {copySuccess ? 'Copied!' : 'Copy'}
          </button>
          <button onClick={handleOpenOpponent} className={styles.openButton}>
            Open as {opponentPlayerId === 'player1' ? 'P1' : 'P2'}
          </button>
        </div>
      </div>

      <details className={styles.instructionsDetails}>
        <summary className={styles.instructionsSummary}>
          Testing Instructions
        </summary>
        <ol className={styles.instructionsList}>
          <li>Click "Open as {opponentPlayerId === 'player1' ? 'P1' : 'P2'}" to open a second window</li>
          <li>Place creatures in both windows - they sync in real-time</li>
          <li>Mark both players ready to start countdown</li>
          <li>Or share the link with another player on a different device</li>
        </ol>
      </details>
    </div>
  );
};

export default MatchSharing;
